var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { chain, chunk, filter, flatMap, keyBy, map as mapFromLodash, range, round, set, sum, sumBy, times, } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY_SYMBOL, CurrencyCode, DifferenceMode, PlanType } from '../../enum';
import { useAuthenticatedAPI } from '../../state';
import { formatNumberByLocale } from '../../util';
import { traceBarConfig, traceStackConfig } from '../common/chart/config';
import { escapePlotlyText } from '../common/chart/plotlyText';
import { QueueType } from '../multiskill/types';
import { DisplayMode } from '../report/resourcing';
import { useDisplayModeParams, useReportAPI } from '../report/selectors';
import { MONTH_OF_YEAR, selectPlanType } from '../wizard/selectors';
import { useSummaryReportAPI } from './summaryReportApi';
import { ConstraintType, } from './types';
import { generateQueueRoleLegend, handleLayout, handleTemplate, replaceNameInTemplate } from './util';
// Re-exported from its leaf module so the many existing `annual/selectors` importers
// keep working; new out-of-report callers should import it from './summaryReportApi'.
export { useSummaryReportAPI } from './summaryReportApi';
var MULTISKILL_API = '/api/multiskill/{planId}';
var HEADCOUNT_CHANGE_API = '/api/plan/:planId/headcount-changes';
var selectScenarios = function (plan) { return plan === null || plan === void 0 ? void 0 : plan.scenarios; };
var selectCurrency = function (plan) { var _a; return (_a = plan === null || plan === void 0 ? void 0 : plan.currency) !== null && _a !== void 0 ? _a : CurrencyCode.Dollar; };
var selectScenarioMap = createSelector(selectScenarios, function (scenario) {
    return keyBy(scenario, function (item) { return (item.isBaseline ? i18next.t('translation:baseline') : item.name); });
});
var createScenarioSelector = function (name) {
    return createSelector(selectScenarioMap, function (map) { return map[name]; });
};
export var selectSummaryReport = function (dataReport) { return dataReport; };
var selectSummaryReportCooked = createSelector(selectSummaryReport, function (dataReport) {
    return dataReport === null || dataReport === void 0 ? void 0 : dataReport.map(function (i) { return (__assign(__assign({}, i), { hasMultiskill: i.defQueue.some(function (q) { return q.group; }) || i.nonDefQueue.some(function (q) { return q.group; }) || i.multiSkill.some(function (q) { return q.group; }) })); });
});
var selectSummaryGroupByType = createSelector(selectSummaryReportCooked, function (dataReport) {
    return chain(dataReport)
        .map(function (i) { return (__assign(__assign({}, i), { multiSkill: i.dataType === QueueType.SingleSkill ? [] : i.multiSkill })); })
        .groupBy('dataType')
        .value();
});
var selectMultiSkillScenarios = createSelector(selectSummaryReportCooked, function (dataReport) {
    return filter(dataReport, { hasMultiskill: true, dataType: QueueType.Multiskill, year: 0 });
});
var createSelectorSummaryReportByType = function (dataType) {
    return createSelector(selectSummaryReportCooked, selectSummaryGroupByType, function (summaryReports, summaryReportGrouped) {
        return dataType ? summaryReportGrouped[dataType] : summaryReports;
    });
};
var selectSummaryChunkSize = createSelector(selectSummaryReport, function (summaryReports) {
    return summaryReports ? filter(summaryReports, { year: 0 }).length : 1;
});
var selectQueuesByName = function (queueName, chunkSize, queueType) {
    return createSelector(selectSummaryReport, function (summaryReports) {
        return chain(summaryReports)
            .flatMap(function (_a) {
            var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue, multiSkill = _a.multiSkill;
            return __spreadArray(__spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true), multiSkill, true);
        })
            .filter({ name: queueName, queueType: queueType })
            .chunk(chunkSize)
            .value();
    });
};
var selectRoleByKey = function (keyItem, chunkSize) {
    return createSelector(selectSummaryReport, function (summaryReports) {
        return chain(summaryReports)
            .flatMap(function (report) { return report[keyItem]; })
            .groupBy('role')
            .map(function (values, role) { return ({ role: role, value: chunk(mapFromLodash(values, 'value'), chunkSize) }); })
            .value();
    });
};
var selectQueuesByCost = createSelector(selectSummaryReport, function (summaryReports) {
    return chain(summaryReports)
        .flatMap(function (_a) {
        var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue;
        return __spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true);
    })
        .sortBy('order')
        .groupBy('name')
        .value();
});
var getSign = function (value) {
    if (value > 0)
        return '+';
    if (value < 0)
        return '-';
    return '';
};
var formatVarianceValue = function (value, percentageUnit, decimalPlaces, currencyPrefix, currencySuffix) {
    var sign = getSign(value);
    var formattedValue = formatNumberByLocale(Math.abs(value), {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
    return "".concat(sign).concat(currencyPrefix).concat(formattedValue).concat(currencySuffix).concat(percentageUnit);
};
var formatShortVarianceValue = function (value, percentageUnit, decimalPlaces, currencyPrefix, currencySuffix) {
    var absoluteValue = Math.abs(value);
    if (absoluteValue <= 9999) {
        return formatVarianceValue(value, percentageUnit, decimalPlaces, currencyPrefix, currencySuffix);
    }
    var divisor = 1000;
    var unit = 'K';
    if (absoluteValue >= 1000000000) {
        divisor = 1000000000;
        unit = 'B';
    }
    else if (absoluteValue >= 1000000) {
        divisor = 1000000;
        unit = 'M';
    }
    var sign = getSign(value);
    var formattedValue = formatNumberByLocale(absoluteValue / divisor, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    });
    return "".concat(sign).concat(currencyPrefix).concat(formattedValue).concat(unit).concat(currencySuffix).concat(percentageUnit);
};
var toFiniteNumber = function (value) {
    if (value === null || value === '')
        return null;
    var numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};
export var generateVariances = function (baseValue, currentValue, differenceMode, options) {
    var _a, _b, _c;
    if (differenceMode === void 0) { differenceMode = DifferenceMode.Absolute; }
    if (options === void 0) { options = {}; }
    baseValue = toFiniteNumber(baseValue);
    currentValue = toFiniteNumber(currentValue);
    if (baseValue === null || currentValue === null) {
        return undefined;
    }
    var diffValue = currentValue - baseValue;
    var percentageUnit = '';
    if (!diffValue)
        return undefined;
    var decimalPlaces = (_a = options.decimalPlaces) !== null && _a !== void 0 ? _a : 1;
    var isPercentageMode = differenceMode === DifferenceMode.Percentage;
    if (isPercentageMode) {
        if (!baseValue)
            return { detailValue: 'X', shortValue: 'X' };
        // Round to display precision so pct mode never pads with trailing zeros.
        diffValue = round((diffValue * 100) / baseValue, decimalPlaces);
        percentageUnit = '%';
    }
    // Currency symbols only make sense for absolute-value diffs, not percentage diffs.
    var currencyPrefix = isPercentageMode ? '' : (_b = options.currencyPrefix) !== null && _b !== void 0 ? _b : '';
    var currencySuffix = isPercentageMode ? '' : (_c = options.currencySuffix) !== null && _c !== void 0 ? _c : '';
    return {
        detailValue: formatVarianceValue(diffValue, percentageUnit, decimalPlaces, currencyPrefix, currencySuffix),
        shortValue: formatShortVarianceValue(diffValue, percentageUnit, decimalPlaces, currencyPrefix, currencySuffix),
    };
};
export var useHeadcountChangeLoadAPI = function (planId) {
    return useAuthenticatedAPI(HEADCOUNT_CHANGE_API, { planId: planId }, 'GET');
};
export var useHeadcountChangeSaveAPI = function (planId) {
    return useAuthenticatedAPI(HEADCOUNT_CHANGE_API, { planId: planId }, 'PUT');
};
export var useSummaryByDisplayMode = function () {
    var data = useSummaryReportAPI()[0].data;
    var displayMode = useDisplayModeParams()[0];
    var dataType = displayMode === DisplayMode.All ? undefined : displayMode;
    var selectSummaryReportByType = useMemo(function () { return createSelectorSummaryReportByType(dataType); }, [dataType]);
    return selectSummaryReportByType(data);
};
export var useMultiskillScenariosAPI = function () {
    var data = useSummaryByDisplayMode();
    return selectMultiSkillScenarios(data);
};
// Returns all multiskill scenarios regardless of current display mode filter
export var useAllMultiskillScenariosAPI = function () {
    var data = useSummaryReportAPI()[0].data;
    return selectMultiSkillScenarios(data);
};
export var useSummaryChunkSize = function () {
    var data = useSummaryByDisplayMode();
    return selectSummaryChunkSize(data);
};
export var useSelectScenario = function (name) {
    if (name === void 0) { name = 'Baseline'; }
    var data = useReportAPI()[0].data;
    var selectScenario = useMemo(function () { return createScenarioSelector(name); }, [name]);
    return selectScenario(data);
};
export var useSelectQueuesByName = function (queueName, constraintType) {
    var data = useSummaryByDisplayMode();
    var chunkSize = useSummaryChunkSize();
    var selectQueues = useMemo(function () {
        return selectQueuesByName(queueName, chunkSize, constraintType === ConstraintType.Multiskill ? QueueType.Multiskill : QueueType.SingleSkill);
    }, [queueName, chunkSize, constraintType]);
    return selectQueues(data);
};
export var useSelectRoles = function (keyItem) {
    var data = useSummaryByDisplayMode();
    var chunkSize = useSummaryChunkSize();
    var selectRoles = useMemo(function () { return selectRoleByKey(keyItem, chunkSize); }, [keyItem, chunkSize]);
    return selectRoles(data);
};
export var useSelectQueuesByCost = function () {
    var data = useSummaryByDisplayMode();
    return selectQueuesByCost(data);
};
export var useSelectCurrency = function () {
    var data = useReportAPI()[0].data;
    return selectCurrency(data);
};
var transformDataArrayForChart = function (items) {
    var _a;
    return times((_a = items[0]) === null || _a === void 0 ? void 0 : _a.data.length, function (index) {
        var y = items.map(function (t) { return t.data[index]; });
        return {
            y: y,
            x: range(items.length),
            text: y.map(String),
            name: items[0].name[index],
            hovertext: items.map(function (t) { return escapePlotlyText(t.xLabel); }),
        };
    });
};
var transformDataForChart = function (items) {
    var y = mapFromLodash(items, 'data');
    return [
        {
            y: y,
            x: range(items.length),
            text: y.map(String),
            hovertext: items.map(function (t) { return escapePlotlyText(t.xLabel); }),
        },
    ];
};
export var useChartPerRow = function () {
    var _a;
    var data = useReportAPI()[0].data;
    var planType = selectPlanType(data);
    var periodLength = (_a = data === null || data === void 0 ? void 0 : data.periodLength) !== null && _a !== void 0 ? _a : 1;
    var currency = data === null || data === void 0 ? void 0 : data.currency;
    var year = planType === PlanType.Monthly ? periodLength / MONTH_OF_YEAR : 1;
    var chartPerRow = year < 3 ? 2 : 1;
    var costPerWorkActivityRow = year < 3 ? 1 : year;
    return { chartPerRow: chartPerRow, costPerWorkActivityRow: costPerWorkActivityRow, year: year, scenarios: data === null || data === void 0 ? void 0 : data.scenarios, currency: currency };
};
export var useChartData = function (extractDataFn, options, isTransformArray) {
    if (isTransformArray === void 0) { isTransformArray = false; }
    var data = useSummaryByDisplayMode();
    var _a = useChartPerRow(), year = _a.year, _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b;
    var colorsByIndex = handleLayout(scenarios).colorsByIndex;
    var _c = handleTemplate(options), texttemplate = _c.texttemplate, hovertemplate = _c.hovertemplate;
    var traceConfig = structuredClone(traceStackConfig);
    if (!isTransformArray) {
        var markerColor = flatMap(colorsByIndex, function (color) { return times(year, function () { return color; }); });
        set(traceConfig, 'marker.color', markerColor);
    }
    var chartData = chain(data)
        .groupBy(function (item) { return "".concat(item.name, ".").concat(item.year); })
        .toPairs()
        .flatMap(function (_a) {
        var values = _a[1];
        var extData = extractDataFn(values);
        var objData = Array.isArray(extData) || typeof extData === 'number' ? { data: extData } : extData;
        return __assign({ year: values[0].year, xLabel: year > 1 ? "".concat(values[0].name, " - ").concat(i18next.t('table.year'), " ").concat(values[0].year + 1) : values[0].name, order: "".concat(values[0].order, ".").concat(values[0].year) }, objData);
    })
        .sortBy('order')
        .thru(function (items) { return (isTransformArray ? transformDataArrayForChart(items) : transformDataForChart(items)); })
        .map(function (item) { return (__assign(__assign(__assign({}, item), traceConfig), { texttemplate: texttemplate, hovertemplate: hovertemplate })); })
        .value();
    return replaceNameInTemplate(chartData);
};
export var useTotalRequiredAgent = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, year = _a.year;
    var legends = handleLayout(scenarios).legends;
    var extractDataFn = function (values) {
        return chain(values)
            .flatMap(function (_a) {
            var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue;
            return __spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true);
        })
            .flatMap('requiredAgents')
            .sum()
            .value();
    };
    var options = {
        isSimple: true,
    };
    var data = useChartData(extractDataFn, options);
    return { data: data, key: 'totalRequiredAgent', middleLegends: year > 1 ? legends : [] };
};
export var useAgentByWorkType = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, year = _a.year;
    var middleLegends = handleLayout(scenarios).legends;
    var getDataAndQueue = function (values) {
        var data = flatMap(values, function (_a) {
            var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue;
            return flatMap(__spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true), 'requiredAgents');
        });
        var name = flatMap(values, function (_a) {
            var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue;
            return flatMap(__spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true), 'name');
        });
        return { data: data, name: name };
    };
    var data = useChartData(getDataAndQueue, {}, true);
    var queue = flatMap(data, 'name');
    var _c = generateQueueRoleLegend(queue), legends = _c.legends, colorsway = _c.colorsway;
    return { data: data, key: 'agentsByWorkType', legends: legends, middleLegends: year > 1 ? middleLegends : [], colorsway: colorsway };
};
export var useSupportStaffRequirement = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, year = _a.year;
    var legends = handleLayout(scenarios).legends;
    var extractDataFn = function (values) {
        return chain(values)
            .flatMap(function (_a) {
            var supportStaff = _a.supportStaff;
            return supportStaff;
        })
            .flatMap('value')
            .sum()
            .value();
    };
    var options = {
        isSimple: true,
    };
    var data = useChartData(extractDataFn, options);
    return { data: data, key: 'supportStaffRequirement', middleLegends: year > 1 ? legends : [] };
};
export var useBudgetSummary = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, currency = _a.currency, year = _a.year;
    var legends = handleLayout(scenarios).legends;
    var currencySymbol = currency ? CURRENCY_SYMBOL[currency] : '';
    var yAxisCurrency = currencySymbol ? "(".concat(currencySymbol, ")") : '';
    var extractDataFn = function (values) {
        return chain(values[0])
            .pick(['agentsSalaries', 'supportStaffSalaries', 'hiringAhead', 'overtimeCosts'])
            .flatMap()
            .sum()
            .value();
    };
    var options = {
        isSimple: true,
        isFormatNumber: true,
        currency: currencySymbol,
    };
    var data = useChartData(extractDataFn, options);
    return { data: data, key: 'budgetSummary', middleLegends: year > 1 ? legends : [], yTickPrefix: yAxisCurrency };
};
export var useSupportStaffByRole = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, year = _a.year;
    var middleLegends = handleLayout(scenarios).legends;
    var getDataAndRole = function (values) {
        var extractValues = function (staff) { return flatMap(staff, 'value'); };
        var extractRoles = function (staff) { return flatMap(staff, 'role'); };
        var data = flatMap(values, function (_a) {
            var supportStaff = _a.supportStaff;
            return extractValues(supportStaff);
        });
        var name = flatMap(values, function (_a) {
            var supportStaff = _a.supportStaff;
            return extractRoles(supportStaff);
        });
        return { data: data, name: name };
    };
    var data = useChartData(getDataAndRole, {}, true);
    var roles = flatMap(data, 'name');
    var _c = generateQueueRoleLegend(roles), legends = _c.legends, colorsway = _c.colorsway;
    return { data: data, key: 'supportStaffByRole', legends: legends, middleLegends: year > 1 ? middleLegends : [], colorsway: colorsway };
};
export var useResourceBudgetDetail = function () {
    var _a = useChartPerRow(), _b = _a.scenarios, scenarios = _b === void 0 ? [] : _b, year = _a.year;
    var t = useTranslation('report', { keyPrefix: 'annual.chart' }).t;
    var middleLegends = handleLayout(scenarios).legends;
    var currency = useChartPerRow().currency;
    var currencySymbol = currency ? CURRENCY_SYMBOL[currency] : '';
    var name = [t('agentsSalaries'), t('supportStaffSalaries'), t('hiringAhead'), t('overtimeCosts')];
    var yAxisCurrency = currencySymbol ? "(".concat(currencySymbol, ")") : '';
    var getDataAndBudget = function (values) {
        var data = chain(values[0])
            .pick(['agentsSalaries', 'supportStaffSalaries', 'hiringAhead', 'overtimeCosts'])
            .flatMap()
            .value();
        return { data: data, name: name };
    };
    var options = {
        isSimple: false,
        isFormatNumber: true,
        currency: currencySymbol,
    };
    var legends = generateQueueRoleLegend(name).legends;
    var data = useChartData(getDataAndBudget, options, true);
    return {
        data: data,
        key: 'resourceBudgetDetail',
        legends: legends,
        yTickPrefix: yAxisCurrency,
        middleLegends: year > 1 ? middleLegends : [],
    };
};
export var useResourcingCharts = function () {
    var totalRequiredAgent = useTotalRequiredAgent();
    var agentByWorkType = useAgentByWorkType();
    var supportStaffRequirement = useSupportStaffRequirement();
    var supportStaffByRole = useSupportStaffByRole();
    var optionalCharts = [supportStaffRequirement, supportStaffByRole].filter(function (_a) {
        var data = _a.data;
        return sumBy(data, function (item) { return sum(item.y); });
    });
    return __spreadArray([totalRequiredAgent, agentByWorkType], optionalCharts, true);
};
export var useFinancialCharts = function () {
    var budgetSummary = useBudgetSummary();
    var resourceBudgetDetail = useResourceBudgetDetail();
    return [budgetSummary, resourceBudgetDetail].filter(function (_a) {
        var data = _a.data;
        return sumBy(data, function (item) { return sum(item.y); });
    });
};
export var useCostPerWorkActivity = function () {
    var reportData = useSummaryByDisplayMode();
    var currency = useChartPerRow().currency;
    var currencySymbol = currency ? CURRENCY_SYMBOL[currency] : '';
    var _a = handleTemplate({ currency: currencySymbol, isCostPerWork: true }), hovertemplate = _a.hovertemplate, texttemplate = _a.texttemplate;
    return chain(reportData)
        .groupBy('year')
        .toPairs()
        .flatMap(function (_a) {
        var year = _a[0], values = _a[1];
        return flatMap(values, function (_a) {
            var defQueue = _a.defQueue, nonDefQueue = _a.nonDefQueue, name = _a.name;
            var queue = chain(__spreadArray(__spreadArray([], defQueue, true), nonDefQueue, true)).sortBy('order');
            return {
                data: queue.flatMap('costPerWork').value(),
                queue: queue.flatMap('name').value(),
                year: year,
                name: name,
            };
        });
    })
        .groupBy('year')
        .thru(function (items) {
        return chain(items)
            .map(function (subItem) {
            return subItem.map(function (_a) {
                var data = _a.data, queue = _a.queue, name = _a.name;
                var queueNames = queue.map(function (queueName) { return escapePlotlyText(queueName); });
                var scenarioName = escapePlotlyText(name);
                return __assign(__assign({}, traceBarConfig), { y: data, x: queueNames, text: data, hovertext: queueNames, name: scenarioName, hovertemplate: hovertemplate.replace('value', function () { return scenarioName; }), texttemplate: texttemplate });
            });
        })
            .filter(function (data) { return sumBy(data, function (item) { return sum(item.y); }) !== 0; })
            .value();
    })
        .value();
};
export var useDeleteMultiskill = function (planId) {
    return useAuthenticatedAPI(MULTISKILL_API, { planId: planId }, 'DELETE');
};
export var useExistMultiskillReport = function () {
    var _a;
    var data = useSummaryReportAPI()[0].data;
    var grouped = selectSummaryGroupByType(data);
    return !!((_a = grouped[QueueType.Multiskill]) === null || _a === void 0 ? void 0 : _a.length);
};
